// src/services/TarifsMapperService.ts
import { TarifsLegalinData, TarifType, TableauTarif } from '../services/scraper_service';

export interface MappedTarif {
    type: string;
    nSType?: number;
    prixAnnexeI_VI: number;
    prixBase: number;
    prixScraped: number[];
    source: 'scraping' | 'hardcoded';
}

export interface TarifsMapping {
    modifications: MappedTarif[];
    constitutions: MappedTarif[];
    dissolutions: MappedTarif[];
    autres: MappedTarif[];
    tarifsCaractere: { [annexe: string]: number };
}

class TarifsMapperService {
    private typeMapping: { [key: string]: { nSType: number; category: string; aliases: string[] } } = {
        // MODIFICATIONS
        'Nomination / Départ / Remplacement d\'administrateur': {
            nSType: 1,
            category: 'modifications',
            aliases: ['administrateur', 'nomination administrateur', 'départ administrateur']
        },
        'Changement de durée': {
            nSType: 2,
            category: 'modifications',
            aliases: ['durée', 'changement durée']
        },
        'Changement de dirigeant': {
            nSType: 3,
            category: 'modifications',
            aliases: ['dirigeant', 'changement dirigeant']
        },
        'Nomination / Départ / Remplacement de CAC': {
            nSType: 4,
            category: 'modifications',
            aliases: ['CAC', 'commissaire aux comptes', 'nomination CAC']
        },
        'Transfert de siège dans le département': {
            nSType: 5,
            category: 'modifications',
            aliases: ['siège département', 'transfert siège département']
        },
        'Transfert de siège hors département': {
            nSType: 6,
            category: 'modifications',
            aliases: ['siège hors département', 'transfert siège hors']
        },
        'Modification d\'objet social': {
            nSType: 7,
            category: 'modifications',
            aliases: ['objet social', 'modification objet']
        },
        'Modification du capital': {
            nSType: 8,
            category: 'modifications',
            aliases: ['capital', 'modification capital']
        },
        'Changement de dénomination': {
            nSType: 9,
            category: 'modifications',
            aliases: ['dénomination', 'changement dénomination']
        },
        'Transformation SARL en SAS': {
            nSType: 10,
            category: 'modifications',
            aliases: ['SARL en SAS', 'transformation SARL SAS']
        },
        'Transformation SARL en SCI': {
            nSType: 11,
            category: 'modifications',
            aliases: ['SARL en SCI', 'transformation SARL SCI']
        },
        'Transformation SAS en SARL': {
            nSType: 12,
            category: 'modifications',
            aliases: ['SAS en SARL', 'transformation SAS SARL']
        },
        'Transformation Autre': {
            nSType: 13,
            category: 'modifications',
            aliases: ['transformation autre', 'autre transformation']
        },
        'Mouvement d\'associé': {
            nSType: 14,
            category: 'modifications',
            aliases: ['associé', 'mouvement associé']
        },

        // CONSTITUTIONS
        'Société anonyme (SA)': {
            nSType: 15,
            category: 'constitutions',
            aliases: ['SA', 'société anonyme', 'anonyme']
        },
        'Société par actions simplifiée (SAS)': {
            nSType: 16,
            category: 'constitutions',
            aliases: ['SAS', 'actions simplifiée']
        },
        'Société par actions simplifiée unipersonnelle (SASU)': {
            nSType: 17,
            category: 'constitutions',
            aliases: ['SASU', 'actions simplifiée unipersonnelle']
        },
        'Société en nom collectif (SNC)': {
            nSType: 18,
            category: 'constitutions',
            aliases: ['SNC', 'nom collectif']
        },
        'Société à responsabilité limitée (SARL)': {
            nSType: 19,
            category: 'constitutions',
            aliases: ['SARL', 'responsabilité limitée']
        },
        'Société à responsabilité limitée unipersonnelle (EURL)': {
            nSType: 20,
            category: 'constitutions',
            aliases: ['EURL', 'responsabilité limitée unipersonnelle']
        },
        'Société civile (à l\'exception des sociétés civiles à objet immobilier)': {
            nSType: 21,
            category: 'constitutions',
            aliases: ['société civile', 'civile exception']
        },
        'Société civile à objet immobilier (SCI)': {
            nSType: 22,
            category: 'constitutions',
            aliases: ['SCI', 'civile immobilier', 'objet immobilier']
        },

        // DISSOLUTIONS
        'Ouverture Dissolution Anticipée': {
            nSType: 23,
            category: 'dissolutions',
            aliases: ['dissolution anticipée', 'ouverture dissolution']
        },
        'Clôture de liquidation': {
            nSType: 24,
            category: 'dissolutions',
            aliases: ['liquidation', 'clôture liquidation']
        },

        // AUTRES
        'Changement de nom patronymique': {
            nSType: 25,
            category: 'autres',
            aliases: ['nom patronymique', 'changement nom']
        }
    };

    private fallbackPrices: { [key: string]: { annexeI_VI: number; base: number } } = {
        'Nomination / Départ / Remplacement d\'administrateur': { annexeI_VI: 108, base: 125 },
        'Changement de durée': { annexeI_VI: 108, base: 125 },
        'Changement de dirigeant': { annexeI_VI: 108, base: 125 },
        'Nomination / Départ / Remplacement de CAC': { annexeI_VI: 108, base: 125 },
        'Transfert de siège dans le département': { annexeI_VI: 108, base: 125 },
        'Transfert de siège hors département': { annexeI_VI: 108, base: 125 },
        'Modification d\'objet social': { annexeI_VI: 135, base: 156 },
        'Modification du capital': { annexeI_VI: 135, base: 156 },
        'Changement de dénomination': { annexeI_VI: 197, base: 227 },
        'Transformation SARL en SAS': { annexeI_VI: 197, base: 227 },
        'Transformation SARL en SCI': { annexeI_VI: 197, base: 227 },
        'Transformation SAS en SARL': { annexeI_VI: 197, base: 227 },
        'Transformation Autre': { annexeI_VI: 197, base: 227 },
        'Mouvement d\'associé': { annexeI_VI: 197, base: 227 },
        'Société anonyme (SA)': { annexeI_VI: 395, base: 462 },
        'Société par actions simplifiée (SAS)': { annexeI_VI: 197, base: 321 },
        'Société par actions simplifiée unipersonnelle (SASU)': { annexeI_VI: 141, base: 165 },
        'Société en nom collectif (SNC)': { annexeI_VI: 218, base: 257 },
        'Société à responsabilité limitée (SARL)': { annexeI_VI: 147, base: 171 },
        'Société à responsabilité limitée unipersonnelle (EURL)': { annexeI_VI: 123, base: 146 },
        'Société civile (à l\'exception des sociétés civiles à objet immobilier)': { annexeI_VI: 220, base: 260 },
        'Société civile à objet immobilier (SCI)': { annexeI_VI: 189, base: 221 },
        'Ouverture Dissolution Anticipée': { annexeI_VI: 152, base: 179 },
        'Clôture de liquidation': { annexeI_VI: 110, base: 128 },
        'Changement de nom patronymique': { annexeI_VI: 57, base: 57 }
    };

    private parsePrice(priceStr: string): number {
        const cleaned = priceStr.replace(/[€\s]/g, '').replace(',', '.');
        const parsed = parseFloat(cleaned);
        return isNaN(parsed) ? 0 : parsed;
    }

    /**
     * 🆕 NOUVELLE MÉTHODE: Parse les tableaux HTML pour extraire TOUS les tarifs
     */
    private parseTableaux(tableaux: TableauTarif[]): Map<string, { annexeI_VI: number; annexeVII: number }> {
        const tarifsMap = new Map<string, { annexeI_VI: number; annexeVII: number }>();

        tableaux.forEach(tableau => {
            if (!tableau.rows || tableau.rows.length === 0) return;

            const headers = tableau.headers || [];
            const hasAnnexeColumns = headers.some(h =>
                h.toLowerCase().includes('annexe') ||
                h.toLowerCase().includes('forfait')
            );

            tableau.rows.forEach(row => {
                if (row.length < 2) return;

                // Colonne 0 = Type d'annonce
                const typeAnnonce = row[0]?.trim();
                if (!typeAnnonce || typeAnnonce.length < 5) return;

                // Chercher les prix dans les colonnes suivantes
                let annexeI_VI = 0;
                let annexeVII = 0;

                for (let i = 1; i < row.length; i++) {
                    const cellValue = row[i]?.trim();
                    if (!cellValue) continue;

                    const prix = this.parsePrice(cellValue);
                    if (prix > 0) {
                        if (i === 1) {
                            annexeI_VI = prix; // Première colonne de prix = Annexe I-VI
                        } else if (i === 2) {
                            annexeVII = prix; // Deuxième colonne = Annexe VII
                        }
                    }
                }

                if (annexeI_VI > 0 || annexeVII > 0) {
                    tarifsMap.set(typeAnnonce, { annexeI_VI, annexeVII });
                }
            });
        });

        console.log(`[TarifsMapper] Tableaux parsés: ${tarifsMap.size} tarifs trouvés`);
        return tarifsMap;
    }

    /**
     * 🆕 AMÉLIORATION: Trouve le type interne correspondant avec matching avancé
     */
    private findInternalType(scrapedType: string, tarifsMap: Map<string, any>): {
        internalType: string;
        mapping: any;
        source: 'exact' | 'alias' | 'fuzzy' | 'tableau';
    } | null {
        const normalized = scrapedType.toLowerCase().trim();

        // 1. Match exact
        for (const [internalType, mapping] of Object.entries(this.typeMapping)) {
            if (internalType.toLowerCase() === normalized) {
                return { internalType, mapping, source: 'exact' };
            }
        }

        // 2. Match par alias
        for (const [internalType, mapping] of Object.entries(this.typeMapping)) {
            if (mapping.aliases.some(alias => normalized.includes(alias.toLowerCase()))) {
                return { internalType, mapping, source: 'alias' };
            }
        }

        // 3. Match fuzzy
        for (const [internalType, mapping] of Object.entries(this.typeMapping)) {
            const similarity = this.calculateSimilarity(normalized, internalType.toLowerCase());
            if (similarity > 0.75) {
                return { internalType, mapping, source: 'fuzzy' };
            }
        }

        // 4. 🆕 Chercher dans les tableaux parsés
        for (const [tableauType] of tarifsMap.entries()) {
            const tableauNormalized = tableauType.toLowerCase();

            for (const [internalType, mapping] of Object.entries(this.typeMapping)) {
                if (tableauNormalized.includes(internalType.toLowerCase().substring(0, 15))) {
                    return { internalType, mapping, source: 'tableau' };
                }
            }
        }

        return null;
    }

    mapScrapedTarifs(scrapedData: TarifsLegalinData | null): TarifsMapping {
        const result: TarifsMapping = {
            modifications: [],
            constitutions: [],
            dissolutions: [],
            autres: [],
            tarifsCaractere: this.extractTarifsCaractere(scrapedData)
        };

        // 🆕 Parser les tableaux en priorité
        const tarifsFromTableaux = scrapedData?.tarifs_forfaitaires
            ? this.parseTableaux(scrapedData.tarifs_forfaitaires)
            : new Map();

        console.log(`[TarifsMapper] ${tarifsFromTableaux.size} tarifs extraits des tableaux`);

        // Pour chaque type interne
        Object.entries(this.typeMapping).forEach(([typeName, { nSType, category }]) => {
            const fallback = this.fallbackPrices[typeName];

            let prixBase = 0;
            let prixAnnexeI_VI = 0;
            let prixScraped: number[] = [];
            let source: 'scraping' | 'hardcoded' = 'hardcoded';

            // 🆕 PRIORITÉ 1: Chercher dans les tableaux
            for (const [tableauType, tableauPrix] of tarifsFromTableaux.entries()) {
                const match = this.findInternalType(tableauType, tarifsFromTableaux);

                if (match && match.internalType === typeName) {
                    prixAnnexeI_VI = tableauPrix.annexeI_VI || 0;
                    prixBase = tableauPrix.annexeVII || tableauPrix.annexeI_VI || 0;

                    if (prixBase === 0 && prixAnnexeI_VI > 0) {
                        // Calculer annexe VII à partir de annexe I-VI (ratio moyen)
                        prixBase = Math.round(prixAnnexeI_VI / 0.864);
                    }

                    prixScraped = [prixBase];
                    source = 'scraping';

                    console.log(`✅ [TarifsMapper] ${typeName} trouvé dans tableaux: ${prixBase}€`);
                    break;
                }
            }

            // PRIORITÉ 2: Chercher dans types_annonce si non trouvé
            if (source === 'hardcoded' && scrapedData?.types_annonce) {
                const scrapedType = scrapedData.types_annonce.find((t: TarifType) => {
                    const match = this.findInternalType(t.type, tarifsFromTableaux);
                    return match && match.internalType === typeName;
                });

                if (scrapedType && scrapedType.prix.length > 0) {
                    const prix = scrapedType.prix.map(p => this.parsePrice(p)).filter(p => p > 0);
                    if (prix.length > 0) {
                        prixBase = Math.max(...prix);
                        prixAnnexeI_VI = Math.round(prixBase * 0.864);
                        prixScraped = prix;
                        source = 'scraping';

                        console.log(`✅ [TarifsMapper] ${typeName} trouvé dans types_annonce: ${prixBase}€`);
                    }
                }
            }

            // PRIORITÉ 3: Fallback seulement si rien trouvé
            if (source === 'hardcoded' && fallback) {
                prixBase = fallback.base;
                prixAnnexeI_VI = fallback.annexeI_VI;
                console.warn(`⚠️ [TarifsMapper] ${typeName} utilise le fallback: ${prixBase}€`);
            }

            const mapped: MappedTarif = {
                type: typeName,
                nSType,
                prixAnnexeI_VI,
                prixBase,
                prixScraped,
                source
            };

            if (category === 'modifications') result.modifications.push(mapped);
            else if (category === 'constitutions') result.constitutions.push(mapped);
            else if (category === 'dissolutions') result.dissolutions.push(mapped);
            else if (category === 'autres') result.autres.push(mapped);
        });

        // Statistiques
        const totalScraped = [...result.modifications, ...result.constitutions, ...result.dissolutions, ...result.autres]
            .filter(t => t.source === 'scraping').length;
        const totalTypes = Object.keys(this.typeMapping).length;

        console.log(`📊 [TarifsMapper] ${totalScraped}/${totalTypes} tarifs scrapés (${Math.round(totalScraped / totalTypes * 100)}%)`);

        return result;
    }

    private extractTarifsCaractere(scrapedData: TarifsLegalinData | null): { [annexe: string]: number } {
        const defaults: { [annexe: string]: number } = {
            'Annexe I': 0.183,
            'Annexe II': 0.189,
            'Annexe III': 0.189,
            'Annexe IV': 0.189,
            'Annexe V': 0.189,
            'Annexe VI': 0.189,
            'Annexe VII': 0.189
        };

        if (!scrapedData || !scrapedData.tarifs_caractere) {
            return defaults;
        }

        scrapedData.tarifs_caractere.forEach(tarif => {
            const match = tarif.match(/annexe\s+([IVX]+)\s*[:–-]?\s*(\d+[.,]\d{1,3})/i);
            if (match) {
                const annexe = `Annexe ${match[1].toUpperCase()}`;
                const prix = this.parsePrice(match[2]);
                if (prix > 0) {
                    defaults[annexe] = prix;
                }
            }
        });

        return defaults;
    }

    generateBasePrices(mappedTarifs: TarifsMapping): { [key: string]: number } {
        const basePrices: { [key: string]: number } = {};

        [...mappedTarifs.modifications, ...mappedTarifs.constitutions,
        ...mappedTarifs.dissolutions, ...mappedTarifs.autres].forEach(tarif => {
            basePrices[tarif.type] = tarif.prixBase;
        });

        return basePrices;
    }

    private calculateSimilarity(str1: string, str2: string): number {
        const longer = str1.length > str2.length ? str1 : str2;
        const shorter = str1.length > str2.length ? str2 : str1;

        if (longer.length === 0) return 1.0;

        const editDistance = this.levenshteinDistance(longer, shorter);
        return (longer.length - editDistance) / longer.length;
    }

    private levenshteinDistance(str1: string, str2: string): number {
        const matrix: number[][] = [];

        for (let i = 0; i <= str2.length; i++) {
            matrix[i] = [i];
        }

        for (let j = 0; j <= str1.length; j++) {
            matrix[0][j] = j;
        }

        for (let i = 1; i <= str2.length; i++) {
            for (let j = 1; j <= str1.length; j++) {
                if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
                    matrix[i][j] = matrix[i - 1][j - 1];
                } else {
                    matrix[i][j] = Math.min(
                        matrix[i - 1][j - 1] + 1,
                        matrix[i][j - 1] + 1,
                        matrix[i - 1][j] + 1
                    );
                }
            }
        }

        return matrix[str2.length][str1.length];
    }
}

export default new TarifsMapperService();